'use client';

import React from 'react';
import Layout from '../../components/Layout';
import SubDocsLayout from '../../components/SubDocsLayout';
import useTranslation from '../../lib/translation';
import SEOHead from '../../components/SEOHead';

const supportItems = [
  { titleKey: 'support.contact.title', descKey: 'support.contact.desc', enTitle: 'Dedicated support', enDesc: 'Reach out anytime for technical assistance or questions related to your service.' },
  { titleKey: 'support.faq.title', descKey: 'support.faq.desc', enTitle: 'Committed response time', enDesc: 'We respond to support requests within 2–3 business days, depending on workload.' },
  { titleKey: 'support.documentation.title', descKey: 'support.documentation.desc', enTitle: 'Priority access', enDesc: 'Customers with an active plan receive emergency contact info (SMS or direct line) for urgent website issues.' },
  { titleKey: 'support.community.title', descKey: 'support.community.desc', enTitle: 'Performance help', enDesc: "If your site needs updates, optimizations, or new features—we're ready to scale with your growth." },
];

const Support = () => {
  const { t, lang } = useTranslation('common');

  return (
    <Layout>
      <SEOHead
        title={lang === 'lt' ? Array.isArray(t('support.pageTitle')) ? (t('support.pageTitle') as string[]).join(' ') : (t('support.pageTitle') as string) : 'Support | LB Websites'}
        description={
          lang === 'lt'
            ? Array.isArray(t('support.description')) ? (t('support.description') as string[]).join(' ') : (t('support.description') as string)
            : 'Learn how LB Websites provides ongoing customer support after purchase. Contact options, emergency access, and committed response times.'
        }
        keywords='website support, technical assistance, maintenance, customer service, help desk, LB Websites support'
        canonicalUrl={lang === 'lt' ? '/lt/sub/pagalba' : '/sub/support'}
        lang={lang}
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'Service',
          name:
            lang === 'lt' ? Array.isArray(t('support.pageTitle')) ? (t('support.pageTitle') as string[]).join(' ') : (t('support.pageTitle') as string) : 'Support | LB Websites',
          description:
            lang === 'lt'
              ? Array.isArray(t('support.description')) ? (t('support.description') as string[]).join(' ') : (t('support.description') as string)
              : 'Learn how LB Websites provides ongoing customer support after purchase. Contact options, emergency access, and committed response times.',
          url: `https://www.lbvisible.com${
            lang === 'lt' ? '/lt/sub/pagalba' : '/sub/support'
          }`,
          inLanguage: lang === 'lt' ? 'lt' : 'en',
          provider: {
            '@type': 'Organization',
            name: 'LB Visible',
          },
        }}
      />

      <SubDocsLayout>
        <h1 className='text-2xl sm:text-3xl font-semibold text-gray-900 dark:text-white mb-4 text-left'>
          {lang === 'lt' ? t('support.heading') : 'Support'}
        </h1>
        <p className='text-base text-gray-600 dark:text-gray-300 mb-10 text-left leading-relaxed'>
          {lang === 'lt'
            ? t('support.subtitle')
            : "Our commitment doesn't end at launch. We provide ongoing support to ensure your website continues performing at its best—and you feel confident every step of the way."}
        </p>

        <ul className='space-y-6'>
          {supportItems.map((item) => (
            <li key={item.titleKey} className='flex gap-4'>
              <span className='flex-shrink-0 w-2 h-2 rounded-full mt-2 bg-gray-400 dark:bg-gray-500' />
              <div>
                <h2 className='font-semibold text-gray-900 dark:text-white mb-1.5'>
                  {lang === 'lt' ? t(item.titleKey) : item.enTitle}
                </h2>
                <p className='text-sm text-gray-600 dark:text-gray-400 leading-relaxed'>
                  {lang === 'lt' ? t(item.descKey) : item.enDesc}
                </p>
              </div>
            </li>
          ))}
        </ul>

        <p className='mt-10 text-sm text-gray-500 dark:text-gray-400 text-left'>
          {lang === 'lt'
            ? 'Reikia pagalbos dabar? Naudokite kontaktų mygtuką.'
            : 'Need help now? Use the contact button.'}
        </p>
      </SubDocsLayout>
    </Layout>
  );
};

export default Support;
